import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import style from './styles.module.scss';
import SubjectNoteReasonForChangeDialog from './subject-note-reason-for-change-dialog';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { SubjectReviewNoteDto } from '@interfaces-clean/subject-monitoring.interface';
import detailStyle from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import SubjectObservationsApi from '@apis/bff-api/subject-monitoring/subject-observations';

const NOTE_MAX_LENGTH = 3000;

export interface EditSubjectNoteDialogProps {
    studyRef: string;
    note: SubjectReviewNoteDto;
    onClose: () => void;
    /** Called after the note has been saved successfully */
    onSubmit: () => void;
    /** Called when the user chooses to convert the note into an observation */
    onConvert: (observationText: string) => void;
}

interface EditSubjectNoteForm {
    note: string;
}

const COMPONENT_NAME = 'edit-subject-note-dialog';

const EditSubjectNoteDialog = ({ studyRef, note, onClose, onSubmit, onConvert }: EditSubjectNoteDialogProps) => {
    const { t } = useTranslation('sm', { keyPrefix: 'monitoringToolkit.editSubjectNoteDialog' });
    const { t: tCommon } = useTranslation('common');

    const { trigger: updateNote, isMutating: isSaving } = SubjectObservationsApi.useUpdate(studyRef);

    const [showReasonDialog, setShowReasonDialog] = useState(false);

    const defaultValues: EditSubjectNoteForm = { note: note.noteText ?? '' };

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
        reset,
        getValues,
    } = useForm<EditSubjectNoteForm>({ mode: 'all', defaultValues });

    useUnsavedChangesTracking({
        area: 'general',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'general',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const isSaveDisabled = !isValid || !isDirty || isSaving || isSubmitting;

    const createdBy = `${stringDateWithoutTimeFromUtc(note.created, 'DD-MMM-YYYY')} by ${note.createdByName}`;
    const updatedBy = `${stringDateWithoutTimeFromUtc(note.updatedDate, 'DD-MMM-YYYY')} by ${note.updatedByName}`;
    const reviewDisplay = note.reviewTitle
        ? `${note.reviewTitle}${note.datasetOpenedOn ? ` - ${note.datasetOpenedOn}` : ''}`
        : '';

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }

        reset(defaultValues);
        onClose();
    };

    const onConvertHandler = () => {
        onConvert(getValues('note'));
    };

    const onSaveHandler = () =>
        handleSubmit(() => {
            setShowReasonDialog(true);
        })();

    const onReasonSubmit = async (reasonForChange: string) => {
        try {
            const response = await updateNote({
                observationId: note.observationId,
                observation: getValues('note'),
                observationDate: note.observationDate,
                reasonForChange,
            });

            if (response?.success) {
                ShowSuccess(tCommon('actionsSuccessfulToast'));
                reset({ note: getValues('note') });
                setShowReasonDialog(false);
                onSubmit();
            } else {
                ShowError(tCommon('errorMessage'));
            }
        } catch {
            ShowError(tCommon('errorMessage'));
        }
    };

    const showDataListing = !note.panelName && !!note.dataListing;
    const panelOrDataListingLabel = showDataListing ? t('fields.dataListing') : t('fields.panel');
    const panelOrDataListingValue = showDataListing ? note.dataListing : (note.panelName ?? 'N/A');

    return (
        <>
            <DraggableDialog
                dataTestId="edit-subject-note-dialog"
                title={t('title')}
                createdUpdatedInfo={<CreatedUpdatedInfo created={createdBy} updated={updatedBy} />}
                onClose={onCloseHandler}
                onSubmit={onSaveHandler}
                primaryBtnLabel={tCommon('save')}
                primaryBtnDisabled={isSaveDisabled}
                secondaryBtnLabel={tCommon('cancel')}
                secondaryBtnDisabled={isSaving || isSubmitting}
                onAdditionalAction={onConvertHandler}
                additionalBtnLabel={t('convertToObservation')}
                additionalBtnDisabled={isSaving || isSubmitting}
                initialWidth={522}
                initialHeight={455}
                bodyPadding="none"
                createdUpdatedBackgroundColour="gray"
            >
                <div className="k-w-full k-h-full k-d-flex-col">
                    <div className={cn(detailStyle.editSiteObservationInfo, 'k-pt-sm k-pb-sm k-pl-md k-pr-md')}>
                        <div className={cn(detailStyle.pipeGap, 'k-shrink-0 k-d-flex k-flex')}>
                            <div className="k-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-3/12 k-gap-2">
                                <div className="k-font-medium">{t('fields.type')}</div>
                                <div className={cn(detailStyle.editSiteObservationTopInfoLabel, 'k-pl-1')}>Note</div>
                            </div>
                            <div className="k-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-9/12 k-gap-2">
                                <div className="k-font-medium">{t('fields.subjectNumber')}</div>
                                <div className={cn(detailStyle.editSiteObservationTopInfoLabel)}>
                                    {note.subjectNumber}
                                </div>
                            </div>
                            {/* eslint-disable-next-line max-len */}
                            <div className="k-flex k-d-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-6/12 k-gap-2">
                                <div className="k-font-medium">{t('fields.dataset')}</div>
                                <div className={cn(detailStyle.editSiteObservationTopInfoLabel)}>
                                    {note.datasetOpenedOn}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className={cn(
                            detailStyle.editSiteObservationInfo,
                            detailStyle.dividerBottom,
                            'k-pt-sm k-pb-sm k-pl-md k-pr-md',
                        )}
                    >
                        <div className={cn(detailStyle.pipeGap, 'k-shrink-0 k-d-flex k-flex')}>
                            <div className="k-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-5/12 k-gap-2">
                                <div className="k-font-medium">{panelOrDataListingLabel}</div>
                                <div className={cn(detailStyle.editSiteObservationTopInfoLabel)}>
                                    {panelOrDataListingValue}
                                </div>
                            </div>
                            <div className="k-flex k-justify-content-self-start k-flex-row k-d-flex k-align-items-center !k-basis-10/12 k-gap-2">
                                <div className="k-font-medium">{t('fields.review')}</div>
                                <div className={cn(detailStyle.editSiteObservationTopInfoLabel)}>{reviewDisplay}</div>
                            </div>
                        </div>
                    </div>

                    <form
                        id="edit-subject-note-form"
                        className="k-w-full k-d-flex k-flex-column k-gap-3"
                        style={{ padding: '8px 11px' }}
                    >
                        <TextAreaController
                            control={control}
                            name="note"
                            data-testid="field-note"
                            label={`${t('fields.note')} *`}
                            maxLength={NOTE_MAX_LENGTH}
                            required
                            readOnly={isSaving || isSubmitting}
                            fillMode="solid"
                            className={cn(style.addNoteInput)}
                        />
                    </form>
                </div>
            </DraggableDialog>
            {showReasonDialog && (
                <SubjectNoteReasonForChangeDialog
                    isSaving={isSaving}
                    onCancel={() => setShowReasonDialog(false)}
                    onSubmit={onReasonSubmit}
                />
            )}
        </>
    );
};

export default EditSubjectNoteDialog;
